/**
 * Provider tree for a loaded config:
 *   ThemeProvider (config.theme → VITE_THEME → chairshare)
 *   └ QueryClientProvider
 *     └ SessionProvider (supabase-js or mock token; API client)
 *       └ TooltipProvider › ToastProvider › RouterProvider
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo } from "react";
import { RouterProvider } from "react-router/dom";
import { SessionProvider } from "../auth/SessionProvider.js";
import type { JoinConfirmationAttempt } from "../auth/joinConfirmation.js";
import { ToastProvider } from "../components/ui/Toast.js";
import { TooltipProvider } from "../components/ui/Tooltip.js";
import type { PortalConfig } from "../lib/config.js";
import { buildTimeThemeName, ThemeProvider } from "../theme/index.js";
import { createAppRouter } from "./router.js";

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          // Auth / permission / not-found / no-database answers never get better on retry.
          const status = (error as { status?: number } | null)?.status;
          if (status && [401, 403, 404, 422, 503].includes(status)) return false;
          return failureCount < 1;
        },
        refetchOnWindowFocus: false,
        staleTime: 15_000,
      },
    },
  });
}

export function App({ config, joinConfirmationAttempt }: { config: PortalConfig; joinConfirmationAttempt?: JoinConfirmationAttempt | null }) {
  const queryClient = useMemo(() => createQueryClient(), []);
  const router = useMemo(() => createAppRouter(), []);
  return (
    <ThemeProvider themeName={config.theme ?? buildTimeThemeName() ?? null}>
      <QueryClientProvider client={queryClient}>
        <SessionProvider config={config} joinConfirmationAttempt={joinConfirmationAttempt}>
          <TooltipProvider>
            <ToastProvider>
              <RouterProvider router={router} />
            </ToastProvider>
          </TooltipProvider>
        </SessionProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
